import produce from 'immer';
import { ALARM_EMAIL_ROUTING_ACTION } from './actions';

const initialState = {
  settings: [],
  holidays: [],
  // Per-company cutover mode (§9). #5614: the 送信モード control is hidden and
  // the default is LIVE (send per routing settings) — matching the backend,
  // where an absent CONFIG item also means LIVE.
  mode: 'LIVE',
  loading: false,
  updating: false,
  type: '',
  error: null,
  // Set when the settings GET failed: the screen must NOT render rows (or the
  // create-prompting empty state) from the empty default — a save built from
  // those defaults would destroy server state.
  loadError: null,
  // settingId echoed by the most recent UPDATE _SUCCESS. The grid's enabled
  // toggle and the edit modal dispatch the SAME update action (takeEvery, so
  // several writes can be in flight): this lets the modal recognize which
  // row's write succeeded and only auto-close on its own.
  lastUpdatedSettingId: null,
  // True while a テスト送信 POST is in flight — used only to disable the
  // test-send button. Kept separate from `updating`/`type` so it never
  // interferes with the create/update auto-close machinery.
  sendingTest: false,
};

const AlarmEmailRouting = (state = initialState, action) => {
  const { type: actionType, payload } = action;

  switch (actionType) {
    case ALARM_EMAIL_ROUTING_ACTION.GET_ALARM_EMAIL_ROUTING_SETTINGS:
      return produce(state, (draft) => {
        draft.loading = true;
        draft.type = actionType;
        draft.error = null;
        draft.loadError = null;
      });

    // One GET fills settings + holidays + mode (§5 combined payload)
    case ALARM_EMAIL_ROUTING_ACTION.GET_ALARM_EMAIL_ROUTING_SETTINGS_SUCCESS:
      return produce(state, (draft) => {
        draft.settings = payload?.settings || [];
        draft.holidays = payload?.holidays || [];
        draft.mode = payload?.mode || 'LIVE';  // #5614: default LIVE
        draft.loading = false;
        draft.type = actionType;
        draft.loadError = null;
      });

    case ALARM_EMAIL_ROUTING_ACTION.GET_ALARM_EMAIL_ROUTING_SETTINGS_ERROR:
      return produce(state, (draft) => {
        draft.loading = false;
        draft.type = actionType;
        draft.error = payload;
        draft.loadError = payload || 'error';
      });

    case ALARM_EMAIL_ROUTING_ACTION.CREATE_ALARM_EMAIL_ROUTING_SETTING:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_SETTING:
    case ALARM_EMAIL_ROUTING_ACTION.DELETE_ALARM_EMAIL_ROUTING_SETTING:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_HOLIDAYS:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_MODE:
      return produce(state, (draft) => {
        draft.updating = true;
        draft.type = actionType;
        draft.error = null;
      });

    case ALARM_EMAIL_ROUTING_ACTION.CREATE_ALARM_EMAIL_ROUTING_SETTING_SUCCESS:
      return produce(state, (draft) => {
        if (payload && payload.settingId) {
          draft.settings.push(payload);
        }
        draft.updating = false;
        draft.type = actionType;
      });

    // Merge the confirmed PUT payload into the list so grid toggles and modal
    // edits persist visually without a full re-fetch (health-monitor pattern).
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_SETTING_SUCCESS:
      return produce(state, (draft) => {
        const index = state.settings.findIndex(
          (x) => x.settingId === payload?.settingId
        );
        if (index >= 0) {
          draft.settings[index] = { ...state.settings[index], ...payload };
        } else if (payload && payload.settingId) {
          draft.settings.push(payload);
        }
        draft.updating = false;
        draft.type = actionType;
        draft.lastUpdatedSettingId = payload?.settingId || null;
      });

    case ALARM_EMAIL_ROUTING_ACTION.DELETE_ALARM_EMAIL_ROUTING_SETTING_SUCCESS:
      return produce(state, (draft) => {
        draft.settings = state.settings.filter(
          (x) => x.settingId !== payload
        );
        draft.updating = false;
        draft.type = actionType;
      });

    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_HOLIDAYS_SUCCESS:
      return produce(state, (draft) => {
        draft.holidays = payload || [];
        draft.updating = false;
        draft.type = actionType;
      });

    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_MODE_SUCCESS:
      return produce(state, (draft) => {
        draft.mode = payload || 'LIVE';  // #5614: default LIVE
        draft.updating = false;
        draft.type = actionType;
      });

    case ALARM_EMAIL_ROUTING_ACTION.CREATE_ALARM_EMAIL_ROUTING_SETTING_ERROR:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_SETTING_ERROR:
    case ALARM_EMAIL_ROUTING_ACTION.DELETE_ALARM_EMAIL_ROUTING_SETTING_ERROR:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_HOLIDAYS_ERROR:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_MODE_ERROR:
      return produce(state, (draft) => {
        draft.updating = false;
        draft.type = actionType;
        draft.error = payload;
      });

    case ALARM_EMAIL_ROUTING_ACTION.CREATE_ALARM_EMAIL_ROUTING_SETTING_CLEAN_STATE:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_SETTING_CLEAN_STATE:
    case ALARM_EMAIL_ROUTING_ACTION.DELETE_ALARM_EMAIL_ROUTING_SETTING_CLEAN_STATE:
    case ALARM_EMAIL_ROUTING_ACTION.UPDATE_ALARM_EMAIL_ROUTING_HOLIDAYS_CLEAN_STATE:
      return produce(state, (draft) => {
        draft.updating = false;
        draft.type = actionType;
      });

    // Test-mail send: only toggles sendingTest. `type` is intentionally left
    // untouched so the modal's auto-close effect (keyed on `type`) never fires
    // for a test send.
    case ALARM_EMAIL_ROUTING_ACTION.SEND_TEST_NOTIFICATION:
      return produce(state, (draft) => {
        draft.sendingTest = true;
      });

    case ALARM_EMAIL_ROUTING_ACTION.SEND_TEST_NOTIFICATION_SUCCESS:
    case ALARM_EMAIL_ROUTING_ACTION.SEND_TEST_NOTIFICATION_ERROR:
      return produce(state, (draft) => {
        draft.sendingTest = false;
      });

    default:
      return state;
  }
};

export default AlarmEmailRouting;
